import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Client } from '../domain/models';
import { getTenants } from '../apis/TenantService';
import { handleSessionExpiry } from '../utils/sessionGuard';
import NoTenantAssigned from '../components/NoTenantAssigned/NoTenantAssigned';

const NO_TENANTS_ERROR = 'No tenants available';

interface ClientContextShape {
  clients: Client[];
  activeClient?: Client;
  setActiveClientId: (id: string) => void;
  loading: boolean;
  error?: string;
  initialized: boolean;
  refreshTenants: (signal?: AbortSignal) => Promise<void>;
}

const ClientContext = createContext<ClientContextShape | undefined>(undefined);

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const { accounts, instance, inProgress } = useMsal();
  // Initialize empty; will populate from getTenants on mount
  const [clients, setClients] = useState<Client[]>([]);
  const [activeClientId, setActiveClientId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [initialized, setInitialized] = useState(false);
  const activeClient = clients.find((c) => c.id === activeClientId);

  const refreshTenants = useCallback(async (signal?: AbortSignal) => {
    const controller = signal ? undefined : new AbortController();
    const resolvedSignal = signal ?? controller?.signal;
    setLoading(true);
    setError(undefined);
    try {
      const tenants = await getTenants(resolvedSignal);
     
      if (resolvedSignal?.aborted) {
        return;
      }
      if (!tenants || tenants.length === 0) {
        console.warn('No tenants returned from API');
        setClients([]);
        setActiveClientId('');
        setError(NO_TENANTS_ERROR);
        return;
      }
      setClients(tenants.map(t => ({ id: t.id, name: t.name, description: '', policyVersion: 'N/A' })));
      setActiveClientId(prev => tenants.find(t => t.id === prev)?.id || tenants[0].id);
    } catch (e) {
      if (resolvedSignal?.aborted) {
        return;
      }
      console.error('Error fetching tenants:', e);
      setClients([]);
      setActiveClientId('');
      setError('Failed to load tenants');
      // Tenant loading failed — clean up MSAL state and redirect to login
      await handleSessionExpiry(instance);
      window.location.href = '/login';
    } finally {
      if (!resolvedSignal?.aborted) {
        setLoading(false);
      }
    }
  }, [instance]);

  const handleNoTenantConfirm = useCallback(async () => {
    await handleSessionExpiry(instance);
    window.location.href = '/login';
  }, [instance]);

  useEffect(() => {
    const activeAccount = instance.getActiveAccount() ?? accounts[0];
    const hasAccount = Boolean(activeAccount);

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!hasAccount) {
      setInitialized(true);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    refreshTenants(controller.signal).finally(() => {
      if (!controller.signal.aborted && !cancelled) {
        setInitialized(true);
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [accounts, instance, inProgress, refreshTenants]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#111827] text-[#f9fafb]">
        <Spin size="large" />
        <span aria-live="polite">Loading tenants...</span>
      </div>
    );
  }

  // Authenticated but resolved to zero tenants — show a dedicated screen instead of a blank workspace.
  if (!loading && clients.length === 0 && error === NO_TENANTS_ERROR) {
    return <NoTenantAssigned onConfirm={handleNoTenantConfirm} />;
  }

  return (
    <ClientContext.Provider value={{ clients, activeClient, setActiveClientId, loading, error, initialized, refreshTenants }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClients = () => {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClients must be used within ClientProvider');
  return ctx;
};
